import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  UploadSimple, ShieldCheck, WarningCircle, CheckCircle, Users, House, Flask as FlaskIcon,
} from "@phosphor-icons/react";

const Stat = ({ label, value, tone = "", testid }) => (
  <div className={`rounded-2xl border p-4 ${tone || "border-border bg-white"}`} data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold">{value}</p>
  </div>
);

const ACTION_CHIP = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  review: "bg-amber-50 text-amber-800 border-amber-200",
};

export default function RosterImport() {
  const fileRef = useRef(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/roster-import");
      setHistory(data);
      const open = data.find((d) => d.status === "preview");
      if (open) {
        const { data: p } = await api.get(`/roster-import/${open.id}/preview`);
        setPlan(p);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/roster-import/upload", body,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPlan(data);
      toast.success(`${data.counts.rows} rows read. Nothing has been written yet.`);
      await loadHistory();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    setBusy("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const dryRun = async () => {
    setBusy("dry");
    try {
      const { data } = await api.post(`/roster-import/${plan.import_id}/dry-run`);
      setPlan((p) => ({ ...p, dry_run: data }));
      toast.success(data.idempotent ? "Dry run passed — a repeat import creates no duplicates"
                                    : "Dry run finished — see the results");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy("");
  };

  const applyNow = async () => {
    setBusy("apply");
    try {
      const { data } = await api.post(`/roster-import/${plan.import_id}/apply`,
        { confirm, reason: "Owner approved the roster import" });
      toast.success(`Imported: ${data.students_created.length} created, ${data.students_updated.length} updated`);
      setConfirm("");
      const { data: p } = await api.get(`/roster-import/${plan.import_id}/preview`);
      setPlan(p);
      await loadHistory();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy("");
  };

  const c = plan?.counts;
  const applied = plan?.status === "applied";

  return (
    <div data-testid="roster-import-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Administrator only</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Roster import</h1>
      <p className="mt-3 text-muted-foreground max-w-3xl">
        Upload a Brightwheel roster export to preview exactly what would change. The file is kept in a
        private server folder, never published, and nothing is written to the live site until you type
        IMPORT below. No invitations, messages, charges, attendance, medication schedules or signed forms
        are ever created here.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden"
               data-testid="roster-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy === "upload"}
                className="h-12 rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]"
                data-testid="roster-upload-btn">
          <UploadSimple size={18} className="mr-2" />
          {busy === "upload" ? "Reading…" : "Upload roster CSV"}
        </Button>
        {plan && !applied && (
          <Button onClick={dryRun} disabled={busy === "dry"} variant="outline"
                  className="h-12 rounded-full font-bold" data-testid="roster-dryrun-btn">
            <FlaskIcon size={18} className="mr-2" />
            {busy === "dry" ? "Running…" : "Run isolated dry run"}
          </Button>
        )}
      </div>

      {!plan && (
        <p className="mt-8 text-sm text-muted-foreground" data-testid="roster-empty">
          No roster is waiting for review.
        </p>
      )}

      {plan && (
        <>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className={`rounded-full border px-3 py-1 text-xs font-bold ${applied
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"}`} data-testid="roster-status">
              {applied ? "Applied to the live site" : "Preview only — nothing written yet"}
            </span>
            <span className="text-xs text-muted-foreground font-semibold">
              {plan.filename} · uploaded by {plan.uploaded_by}
            </span>
          </div>

          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            <Stat label="Roster rows" value={c.rows} testid="stat-rows" />
            <Stat label="To create" value={c.create} testid="stat-create"
                  tone="border-emerald-200 bg-emerald-50" />
            <Stat label="To update" value={c.update} testid="stat-update"
                  tone={c.update ? "border-blue-200 bg-blue-50" : ""} />
            <Stat label="Held for review" value={c.review} testid="stat-review"
                  tone={c.review ? "border-amber-200 bg-amber-50" : ""} />
            <Stat label="With classroom" value={c.with_classroom} testid="stat-classrooms" />
            <Stat label="Needs classroom" value={c.needs_classroom_review} testid="stat-unassigned"
                  tone={c.needs_classroom_review ? "border-amber-200 bg-amber-50" : ""} />
            <Stat label="Birthdates" value={`${c.with_birthdate}/${c.rows}`} testid="stat-birthdates" />
          </div>

          <Tabs defaultValue="students" className="mt-8">
            <TabsList data-testid="roster-tabs">
              <TabsTrigger value="students" data-testid="tab-students">Students ({c.rows})</TabsTrigger>
              <TabsTrigger value="rooms" data-testid="tab-rooms">Classrooms ({plan.rooms.length})</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Adults ({c.adults})</TabsTrigger>
              <TabsTrigger value="issues" data-testid="tab-issues">
                Conflicts &amp; missing ({c.conflicts})
              </TabsTrigger>
              <TabsTrigger value="tests" data-testid="tab-tests">Test results</TabsTrigger>
            </TabsList>

            <TabsContent value="students" className="mt-6">
              <div className="bg-white rounded-3xl border border-border overflow-x-auto">
                <table className="w-full text-sm" data-testid="roster-student-table">
                  <thead className="bg-slate-50 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    <tr>
                      {["Row", "Child", "Action", "Classroom", "Birthdate", "Flags"].map((h) => (
                        <th key={h} className="text-left px-4 py-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plan.rows.map((r) => (
                      <tr key={r.brightwheel_id || r.row} className="border-t border-border align-top"
                          data-testid={`roster-row-${r.row}`}>
                        <td className="px-4 py-3 text-muted-foreground">{r.row}</td>
                        <td className="px-4 py-3 font-bold">{r.name}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${ACTION_CHIP[r.action]}`}>
                            {r.action === "create" ? "Create" : r.action === "update" ? "Match & fill" : "Review"}
                          </span>
                          {r.match_name && <p className="mt-1 text-xs text-muted-foreground">↳ {r.match_name} ({r.match_basis})</p>}
                        </td>
                        <td className="px-4 py-3">
                          {r.room}
                          <p className="text-xs text-muted-foreground">{r.room_reason}</p>
                        </td>
                        <td className="px-4 py-3">
                          {r.birthday || <span className="text-amber-700 font-bold">Missing</span>}
                        </td>
                        <td className="px-4 py-3 max-w-md">
                          {r.flags.length === 0 && <span className="text-muted-foreground">—</span>}
                          <ul className="space-y-1">
                            {r.flags.map((f) => <li key={f} className="text-xs">• {f}</li>)}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="rooms" className="mt-6">
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {plan.rooms.map((r) => (
                  <div key={r.room} className="bg-white rounded-2xl border border-border p-5"
                       data-testid={`roster-room-${r.room}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-display text-lg font-semibold">{r.room}</p>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-bold shrink-0 ${
                        r.action === "existing" ? "bg-blue-50 text-blue-700 border-blue-200"
                                                : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                        {r.action === "existing" ? "Already exists" : "Create"}
                      </span>
                    </div>
                    <p className="mt-3 text-sm font-bold">{r.student_count} child{r.student_count === 1 ? "" : "ren"}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      From roster value{r.raw_variants.length > 1 ? "s" : ""}: {r.raw_variants.map((v) => `"${v}"`).join(", ")}
                    </p>
                    {r.review_room && (
                      <p className="mt-3 text-xs font-bold text-amber-700">
                        Temporary placement room — not used for attendance or scheduling.
                      </p>
                    )}
                    {r.action === "create" && !r.review_room && (
                      <p className="mt-3 text-xs font-bold text-amber-700">
                        Setup required — capacity, ratio, teacher and schedule left empty for you.
                      </p>
                    )}
                    {r.existing_setup && (
                      <p className="mt-3 text-xs text-muted-foreground">
                        Existing settings kept (capacity {r.existing_setup.capacity ?? "not set"}, ratio{" "}
                        {r.existing_setup.max_ratio ? `1:${r.existing_setup.max_ratio}` : "not set"}).
                      </p>
                    )}
                    <ul className="mt-3 text-xs text-muted-foreground">
                      {r.students.map((s) => <li key={s}>• {s}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="mt-6">
              <div className="rounded-2xl border-2 border-[#3B82F6] bg-blue-50/60 p-4 flex items-start gap-3">
                <ShieldCheck size={20} className="text-[#3B82F6] shrink-0 mt-0.5" />
                <p className="text-sm">
                  Every adult below arrives with <strong>no login, no billing, no legal-guardian status and
                  no checkout permission</strong>. An administrator has to verify each one before staff can
                  release a child to them. {c.adults_with_email} of {c.adults} have an email address.
                  Contacts already verified on the website are never downgraded.
                </p>
              </div>
              <div className="mt-5 bg-white rounded-3xl border border-border overflow-x-auto">
                <table className="w-full text-sm" data-testid="roster-contact-table">
                  <thead className="bg-slate-50 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    <tr>{["Adult", "Imported as", "Email", "Phone", "Children", "Notes"].map((h) => (
                      <th key={h} className="text-left px-4 py-3">{h}</th>))}</tr>
                  </thead>
                  <tbody>
                    {plan.contacts.map((a) => (
                      <tr key={a.key} className="border-t border-border" data-testid={`roster-contact-${a.key}`}>
                        <td className="px-4 py-3 font-bold">{a.name}</td>
                        <td className="px-4 py-3">{a.relationship} — unverified</td>
                        <td className="px-4 py-3">{a.email || <span className="text-muted-foreground">none</span>}</td>
                        <td className="px-4 py-3">{a.phone || <span className="text-muted-foreground">none</span>}</td>
                        <td className="px-4 py-3">{a.children.join(", ")}</td>
                        <td className="px-4 py-3 text-xs">
                          {a.already_verified ? "Already verified on the website — left as is"
                            : a.needs_review ? "No email or phone — confirm identity before verifying"
                            : a.existing_link ? "Already linked — blanks filled only" : "New contact"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="issues" className="mt-6 space-y-6">
              <div>
                <h2 className="text-base md:text-lg font-bold">Conflicts to review</h2>
                {plan.rows.every((r) => r.conflicts.length === 0) ? (
                  <p className="mt-3 text-sm text-muted-foreground" data-testid="no-conflicts">
                    No conflicts — nothing on the website would be overwritten.
                  </p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {plan.rows.filter((r) => r.conflicts.length).map((r) => (
                      <div key={r.row} className="bg-white rounded-2xl border border-amber-200 p-4"
                           data-testid={`conflict-${r.row}`}>
                        <p className="font-bold">{r.name}</p>
                        {r.conflicts.map((cf) => (
                          <p key={cf.field} className="mt-2 text-sm">
                            <strong>{cf.field}</strong>: roster says "{String(cf.csv)}", website has
                            "{String(cf.website)}" — {cf.issue}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-base md:text-lg font-bold">Missing information</h2>
                <div className="mt-4 bg-white rounded-3xl border border-border overflow-x-auto">
                  <table className="w-full text-sm" data-testid="missing-table">
                    <thead className="bg-slate-50 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      <tr><th className="text-left px-4 py-3">Child</th>
                        <th className="text-left px-4 py-3">Not supplied by the roster</th></tr>
                    </thead>
                    <tbody>
                      {plan.rows.map((r) => (
                        <tr key={r.row} className="border-t border-border">
                          <td className="px-4 py-3 font-bold">{r.name}</td>
                          <td className="px-4 py-3">{r.missing.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="tests" className="mt-6">
              {!plan.dry_run && (
                <p className="text-sm text-muted-foreground" data-testid="no-dryrun">
                  Run the isolated dry run to prove the counts and that a repeat import creates no duplicates.
                </p>
              )}
              {plan.dry_run && (
                <div className="space-y-4" data-testid="dryrun-results">
                  <div className={`rounded-2xl border-2 p-4 ${plan.dry_run.idempotent
                    ? "border-emerald-300 bg-emerald-50" : "border-[#EF4444] bg-red-50"}`}>
                    <p className="flex items-center gap-2 font-bold">
                      {plan.dry_run.idempotent ? <CheckCircle size={20} weight="bold" className="text-emerald-700" />
                                               : <WarningCircle size={20} weight="bold" className="text-red-700" />}
                      {plan.dry_run.idempotent
                        ? "Idempotency check passed — running the import twice changed nothing the second time"
                        : "Idempotency check FAILED — a second run changed the data"}
                    </p>
                    <p className="mt-2 text-sm">
                      Ran in an isolated test database, which was dropped afterwards. Live data was untouched:{" "}
                      {plan.dry_run.live_untouched.students} students and {plan.dry_run.live_untouched.rooms} classrooms
                      before and after.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {["pass_1", "pass_2"].map((key, i) => (
                      <div key={key} className="bg-white rounded-2xl border border-border p-5"
                           data-testid={`dryrun-${key}`}>
                        <p className="font-bold">{i === 0 ? "First pass" : "Second pass (repeat)"}</p>
                        <dl className="mt-3 space-y-1 text-sm">
                          {[["Students created", plan.dry_run[key].created],
                            ["Students matched & filled", plan.dry_run[key].updated],
                            ["Held for review", plan.dry_run[key].held],
                            ["Classrooms created", plan.dry_run[key].rooms_created.length],
                            ["Contacts linked", plan.dry_run[key].contacts_linked],
                            ["Students in database", plan.dry_run[key].totals.students]].map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-3">
                              <dt className="text-muted-foreground font-semibold">{k}</dt>
                              <dd className="font-bold">{v}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-2xl border border-border p-5">
                    <p className="font-bold flex items-center gap-2"><House size={18} /> Result checks</p>
                    <dl className="mt-3 space-y-1 text-sm">
                      {[["Roster rows imported", plan.dry_run.imported_students],
                        ["Duplicate external IDs", plan.dry_run.duplicate_external_ids],
                        ["Birthdates stored", plan.dry_run.birthdates_stored],
                        ["Classrooms needing setup", plan.dry_run.rooms_needing_setup.join(", ") || "none"],
                        ["Temporary review rooms", plan.dry_run.review_rooms.join(", ") || "none"]].map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3">
                          <dt className="text-muted-foreground font-semibold">{k}</dt>
                          <dd className="font-bold text-right">{v}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="mt-4 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      Children per classroom
                    </p>
                    <ul className="mt-2 text-sm">
                      {Object.entries(plan.dry_run.students_per_room).map(([room, n]) => (
                        <li key={room} className="flex justify-between gap-3">
                          <span>{room}</span><span className="font-bold">{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {!applied && (
            <div className="mt-10 rounded-3xl border-2 border-[#FBBF24] bg-amber-50/60 p-6 max-w-2xl"
                 data-testid="apply-panel">
              <p className="flex items-center gap-2 font-bold"><Users size={20} /> Apply to the live site</p>
              <p className="mt-2 text-sm">
                This writes {c.create} new child{c.create === 1 ? "" : "ren"}, fills blanks on {c.update} existing
                record{c.update === 1 ? "" : "s"}, creates {c.rooms_to_create} classroom{c.rooms_to_create === 1 ? "" : "s"},
                and links {c.adults} unverified adult contact{c.adults === 1 ? "" : "s"}. Existing attendance,
                billing, forms, health records, family links, notes and audit history are untouched.
              </p>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                     placeholder="Type IMPORT to confirm" className="mt-4 h-11 rounded-xl max-w-xs"
                     data-testid="apply-confirm" />
              <Button onClick={applyNow} disabled={busy === "apply" || confirm.trim().toUpperCase() !== "IMPORT"}
                      className="mt-4 h-11 rounded-full font-bold bg-[#10B981] hover:bg-[#059669]"
                      data-testid="apply-btn">
                {busy === "apply" ? "Importing…" : "Apply import"}
              </Button>
            </div>
          )}

          {applied && plan.results && (
            <div className="mt-10 rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-6 max-w-2xl"
                 data-testid="applied-panel">
              <p className="font-bold">Imported on {plan.applied_at?.slice(0, 16).replace("T", " ")}</p>
              <p className="mt-2 text-sm">
                {plan.results.students_created.length} created · {plan.results.students_updated.length} matched
                and filled · {plan.results.rooms_created.length} classroom(s) created ·{" "}
                {plan.results.contacts_linked} unverified contact(s) linked ·{" "}
                {plan.results.held_for_review.length} held for review
              </p>
            </div>
          )}
        </>
      )}

      {history.length > 0 && (
        <div className="mt-10" data-testid="import-history">
          <h2 className="text-base md:text-lg font-bold">Import history</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {history.map((h) => (
              <li key={h.id} className="flex flex-wrap justify-between gap-3 bg-white rounded-2xl border border-border px-4 py-3">
                <span className="font-bold">{h.filename}</span>
                <span className="text-muted-foreground">
                  {h.status === "applied" ? `applied ${h.applied_at?.slice(0, 10)}` : "preview only"} ·
                  uploaded by {h.uploaded_by_name} · {h.uploaded_at?.slice(0, 10)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
